import React from 'react';
import { fireEvent, render, screen, waitFor } from '@testing-library/react';

import ConfigProvider from 'components/ConfigProvider';

import AccountsField from './AccountsField';
import { ACCOUNT_STEP } from './copy';

const OPTIONS = [
  { label: 'Alpha Fleet', value: 7 },
  { label: 'Beta Fleet', value: 8 },
];

const onChange = jest.fn();
const onSearch = jest.fn();
const onLoadMore = jest.fn();

const field = (props = {}) => (
  <ConfigProvider>
    <AccountsField
      options={OPTIONS}
      selected={[]}
      total={OPTIONS.length}
      onChange={onChange}
      onLoadMore={onLoadMore}
      onSearch={onSearch}
      {...props}
    />
  </ConfigProvider>
);

const renderField = (props = {}) => render(field(props));

/**
 * Scrolls OptionsList's own <ul> to `remaining` px from its bottom.
 *
 * jsdom lays nothing out, so the three dimensions the handler reads are defined
 * here; without scrollHeight the handler bails, which is the guard that stops it
 * fetching on open.
 */
const scrollListTo = (remaining) => {
  const list = document.querySelector('ul');
  const clientHeight = 100;
  const scrollHeight = 400;

  Object.defineProperty(list, 'clientHeight', {
    configurable: true,
    value: clientHeight,
  });
  Object.defineProperty(list, 'scrollHeight', {
    configurable: true,
    value: scrollHeight,
  });
  Object.defineProperty(list, 'scrollTop', {
    configurable: true,
    value: scrollHeight - clientHeight - remaining,
    writable: true,
  });

  fireEvent.scroll(list);
};

// The field itself is the text entry — filtering happens by typing in it, as the
// mockup draws. Found by label rather than by role for the reason below.
const input = () => screen.getByLabelText(ACCOUNT_STEP.label);

const chevron = () =>
  screen.getByLabelText(new RegExp(ACCOUNT_STEP.toggle, 'i'));

// Options are clicked by label rather than role: getByRole computes
// accessibility, which calls getComputedStyle, and jsdom's selector engine throws
// on KeyDS's sibling-combinator rules. A jsdom limitation, not the component's —
// the same query works in a browser.

// The footer stands in for the menu in open/closed assertions: it is there
// whatever the options are, including while a search matches nothing.
const applyButton = () => screen.queryByRole('button', { name: /^apply$/i });

const open = async () => {
  fireEvent.focus(input());

  await waitFor(() => expect(applyButton()).toBeInTheDocument());
};

beforeEach(() => {
  onChange.mockClear();
  onSearch.mockClear();
  onLoadMore.mockClear();
});

describe('management/Users AccountsField', () => {
  it('starts collapsed', () => {
    renderField();

    expect(input()).toHaveAttribute('aria-expanded', 'false');
    expect(applyButton()).not.toBeInTheDocument();
  });

  // The mockup's menu holds the options and the footer: filtering is done in the
  // field (787:76816).
  //
  // querySelectorAll rather than getAllByRole for the jsdom reason noted above:
  // resolving the textbox role computes styles, which nwsapi cannot do against
  // KeyDS's sibling-combinator rules.
  it('has no search box inside the menu', async () => {
    renderField();

    await open();

    const textInputs = document.querySelectorAll(
      'input:not([type="checkbox"]):not([type="radio"])',
    );

    expect(textInputs).toHaveLength(1);
    expect(textInputs[0]).toBe(input());
    expect(input()).toHaveAttribute('aria-expanded', 'true');
  });

  it('toggles from the chevron', async () => {
    renderField();

    await open();
    fireEvent.click(chevron());

    await waitFor(() => expect(applyButton()).not.toBeInTheDocument());
  });

  it("carries the design's hint", () => {
    renderField();

    expect(screen.getByText(ACCOUNT_STEP.help)).toBeInTheDocument();
  });

  // Nothing else on screen says the field can be typed into, and against hundreds
  // of accounts searching is how one outside the loaded page gets found.
  describe('the search affordance', () => {
    it('invites a search while the field is empty', () => {
      renderField();

      expect(input()).toHaveAttribute(
        'placeholder',
        ACCOUNT_STEP.searchPlaceholder,
      );
    });

    it('says so in the hint as well', () => {
      renderField();

      expect(screen.getByText(ACCOUNT_STEP.help)).toHaveTextContent(/search/i);
    });

    // By then the field has visibly been used, and the tags need the room.
    it('drops the placeholder once accounts are tagged', () => {
      renderField({ selected: [7] });

      expect(input()).not.toHaveAttribute('placeholder');
    });
  });

  it('lists the accounts when opened', async () => {
    renderField();

    await open();

    expect(screen.getByText('Alpha Fleet')).toBeInTheDocument();
    expect(screen.getByText('Beta Fleet')).toBeInTheDocument();
  });

  // Staged behind APPLY, so ticking a box alone must not reach the caller.
  it('reports nothing until APPLY', async () => {
    renderField();

    await open();
    fireEvent.click(screen.getByText('Alpha Fleet'));

    expect(onChange).not.toHaveBeenCalled();
  });

  it('reports the ticked accounts on APPLY', async () => {
    renderField();

    await open();
    fireEvent.click(screen.getByText('Alpha Fleet'));
    fireEvent.click(screen.getByRole('button', { name: /^apply$/i }));

    expect(onChange).toHaveBeenCalledWith(['7']);
  });

  it('empties the selection from CLEAR', async () => {
    renderField({ selected: [7] });

    await open();
    fireEvent.click(screen.getByRole('button', { name: /^clear$/i }));

    expect(onChange).toHaveBeenCalledWith([]);
  });

  it('leaves the selection alone on CANCEL', async () => {
    renderField({ selected: [7] });

    await open();
    fireEvent.click(screen.getByText('Beta Fleet'));
    fireEvent.click(screen.getByRole('button', { name: /^cancel$/i }));

    expect(onChange).not.toHaveBeenCalled();
  });

  it('closes after applying', async () => {
    renderField();

    await open();
    fireEvent.click(screen.getByRole('button', { name: /^apply$/i }));

    await waitFor(() => expect(applyButton()).not.toBeInTheDocument());
  });

  // Closed, so the only "Alpha Fleet" on screen is the field's own tag.
  it('names the chosen accounts on the field', () => {
    renderField({ selected: [7] });

    expect(screen.getByText('Alpha Fleet')).toBeInTheDocument();
  });

  // A search narrows the options, but the field must keep naming what is applied
  // even when those rows are no longer in the list.
  it('keeps naming an applied account that the options no longer contain', () => {
    renderField({ options: [OPTIONS[1]], selected: [7] });

    expect(screen.getByText('7')).toBeInTheDocument();
  });

  it('searches server side as the admin types in the field', async () => {
    renderField();

    fireEvent.change(input(), { target: { value: 'beta' } });

    await waitFor(() => expect(onSearch).toHaveBeenCalledWith('beta'));
  });

  // Otherwise the first keystroke would narrow a list nobody can see.
  it('opens the menu when typing into a closed field', async () => {
    renderField();

    fireEvent.change(input(), { target: { value: 'beta' } });

    await waitFor(() => expect(applyButton()).toBeInTheDocument());
  });

  it('forgets the query when it closes', async () => {
    renderField();

    await open();
    fireEvent.change(input(), { target: { value: 'beta' } });
    fireEvent.click(screen.getByRole('button', { name: /^cancel$/i }));

    await waitFor(() => expect(input()).toHaveValue(''));
  });

  // Otherwise the next open shows an empty search box over a still-narrowed list,
  // with a status line describing the abandoned search.
  it('drops the filter behind the box, not just the box', async () => {
    renderField();

    await open();
    fireEvent.change(input(), { target: { value: 'beta' } });
    onSearch.mockClear();
    fireEvent.click(screen.getByRole('button', { name: /^cancel$/i }));

    await waitFor(() => expect(onSearch).toHaveBeenCalledWith(''));
  });

  // No filter was applied, so there is nothing to undo and no request to make.
  it('does not re-search when it closes unsearched', async () => {
    renderField();

    await open();
    fireEvent.click(screen.getByRole('button', { name: /^cancel$/i }));

    expect(onSearch).not.toHaveBeenCalled();
  });

  // An OEM admin can administer thousands, so the menu holds a page at a time.
  describe('paging', () => {
    const paged = { hasMore: true, total: 412 };

    it('asks for the next page as the list nears its bottom', async () => {
      renderField(paged);

      await open();
      scrollListTo(10);

      expect(onLoadMore).toHaveBeenCalled();
    });

    it('leaves it alone while the bottom is still far off', async () => {
      renderField(paged);

      await open();
      scrollListTo(300);

      expect(onLoadMore).not.toHaveBeenCalled();
    });

    // jsdom reports every dimension as 0, which without the scrollHeight guard
    // reads as "already at the bottom" — so opening the menu would fetch.
    it('does not fetch merely because the menu opened', async () => {
      renderField(paged);

      await open();
      fireEvent.scroll(document.querySelector('ul'));

      expect(onLoadMore).not.toHaveBeenCalled();
    });

    it('stops listening once everything is loaded', async () => {
      renderField({ hasMore: false, total: OPTIONS.length });

      await open();
      scrollListTo(10);

      expect(onLoadMore).not.toHaveBeenCalled();
    });

    // The <ul> is capped at 340px wide by default, which left the rows and their
    // scrollbar stopping short of the panel's right edge.
    it('lets the list fill the panel so the scrollbar sits on its edge', async () => {
      renderField(paged);

      await open();

      const list = document.querySelector('ul');

      expect(list).toHaveStyle({ maxWidth: '100%' });
      expect(list).toHaveStyle({ minWidth: '100%' });
    });

    /**
     * Regression: search for something, clear it, and paging was dead.
     *
     * A search matching nothing renders the empty state in the list's place, so the
     * <ul> unmounts. AccountsPanel does not, so a ref resolved once on the panel
     * kept pointing at the detached node, and the new <ul> that arrived when the
     * search was cleared had no listener on it.
     */
    it('keeps paging after a search empties the list and is cleared', async () => {
      const { rerender } = renderField(paged);
      await open();

      // The search matches nothing: the empty state replaces the list.
      rerender(field({ ...paged, options: [] }));
      expect(document.querySelector('ul')).not.toBeInTheDocument();

      // Cleared: a new list, and a new <ul>.
      rerender(field(paged));
      scrollListTo(10);

      expect(onLoadMore).toHaveBeenCalled();
    });

    it('reports that it is fetching more', async () => {
      renderField({ ...paged, isLoading: true });

      await open();

      expect(screen.getByText(ACCOUNT_STEP.loadingMore)).toBeInTheDocument();
    });
  });

  // Dropped from the wizard: it could only ever take the page that happens to be
  // loaded, and a control that says "all" while assigning 25 of 236 cannot be
  // worded well. Every account is ticked one at a time; the count below the list
  // is what says how many there are. Asserted for both paging states, since
  // whether the last page has arrived used to decide whether the row appeared.
  describe('select-all', () => {
    it.each([
      ['while pages remain', { hasMore: true, total: 412 }],
      ['once everything matching is loaded', { hasMore: false, total: 2 }],
    ])('offers no such row %s', async (_name, props) => {
      renderField(props);

      await open();

      expect(screen.queryByText(/select all/i)).not.toBeInTheDocument();
      // One row per account and no synthetic row above them. querySelectorAll for
      // the jsdom reason noted above.
      expect(document.querySelectorAll('input[type="checkbox"]')).toHaveLength(
        OPTIONS.length,
      );
    });
  });

  // The only thing saying the list is partial — without it, 25 of 236 accounts
  // look like all of them.
  describe('the count', () => {
    it('says how many of the total are showing', async () => {
      renderField({ hasMore: true, total: 236 });

      await open();

      expect(
        screen.getByText(ACCOUNT_STEP.paged(OPTIONS.length, 236)),
      ).toBeInTheDocument();
    });

    it('says nothing once the list is complete', async () => {
      renderField({ hasMore: false, total: OPTIONS.length });

      await open();

      expect(screen.queryByText(/showing/i)).not.toBeInTheDocument();
    });

    it('gives way to the loading line while a page is in flight', async () => {
      renderField({ hasMore: true, isLoading: true, total: 236 });

      await open();

      expect(screen.getByText(ACCOUNT_STEP.loadingMore)).toBeInTheDocument();
      expect(
        screen.queryByText(ACCOUNT_STEP.paged(OPTIONS.length, 236)),
      ).not.toBeInTheDocument();
    });
  });

  it('says so when there are no accounts to offer', async () => {
    renderField({ options: [] });

    fireEvent.focus(input());

    await waitFor(() =>
      expect(screen.getByText(ACCOUNT_STEP.empty)).toBeInTheDocument(),
    );
  });
});
